import { Pickable } from '@app/engine/core/interfaces';
import { EventBus } from '@app/engine/events/event-bus';
import { EventBusEvent } from '@app/engine/events/event-bus-events';
import { html } from '@app/engine/utils/development/formatter';
import { hideEl } from '@app/engine/utils/get-el';
import { keepTrackApi } from '@app/keepTrackApi';
import { SpaceObjectType } from '@ootk/src/main';
import { CameraType } from '../../camera/camera';
import { errorManagerInstance } from '../../utils/errorManager';
import { ColorScheme } from './color-scheme';
export class ObjectTypeColorScheme extends ColorScheme {
    label = 'Object Type';
    id = 'ObjectTypeColorScheme';
    static id = 'ObjectTypeColorScheme';
    static uniqueObjectTypeFlags = {
        payload: true,
        rocketBody: true,
        debris: true,
        notional: true,
        unknown: true,
        analyst: true,
        facility: true,
        sensor: true,
        missile: true,
        missileInview: true,
        pink: true,
        inFOV: true,
        marker: true,
    };
    static uniqueColorTheme = {
        celestrakDefaultActivePayload: [0.0, 1.0, 0.0, 0.85],
        celestrakDefaultInactivePayload: [1.0, 0.5, 0.0, 1.0],
        celestrakDefaultRocketBody: [1.0, 0.0, 0.0, 1.0],
        celestrakDefaultDebris: [0.5, 0.5, 0.5, 0.9],
        celestrakDefaultSensor: [0.0, 0.0, 1.0, 0.85],
        celestrakDefaultFov: [0.0, 0.0, 1.0, 0.85],
        celestrakDefaultUnknown: [1, 1, 1, 0.85],
    };
    constructor() {
        super(ObjectTypeColorScheme.uniqueColorTheme);
        this.objectTypeFlags = {
            ...this.objectTypeFlags, ...ObjectTypeColorScheme.uniqueObjectTypeFlags,
        };
        EventBus.getInstance().on(EventBusEvent.layerUpdated, () => {
            if (settingsManager.isDisableSensors) {
                this.objectTypeFlags.sensor = false;
                this.objectTypeFlags.inFOV = false;
            }
            if (settingsManager.isDisableLaunchSites) {
                this.objectTypeFlags.facility = false;
            }
            if (!settingsManager.plugins?.MissilePlugin?.enabled) {
                this.objectTypeFlags.missile = false;
                const missileBox = document.querySelector('.layers-missile-box')?.parentElement;
                if (missileBox) {
                    hideEl(missileBox);
                }
            }
        });
    }
    update(obj) {
        /*
         * NOTE: The order of these checks is important
         */
        if (obj.type === SpaceObjectType.TERRESTRIAL_PLANET ||
            obj.type === SpaceObjectType.GAS_GIANT ||
            obj.type === SpaceObjectType.ICE_GIANT ||
            obj.type === SpaceObjectType.DWARF_PLANET ||
            obj.type === SpaceObjectType.MOON) {
            return {
                color: obj.color,
                pickable: Pickable.Yes,
            };
        }
        if ((obj.source ?? '') === 'OEM Import') {
            return {
                color: [1.0, 0.5, 0.0, 1.0],
                pickable: Pickable.Yes,
            };
        }
        if (settingsManager.maxZoomDistance > 2e6) {
            // If zoomed out beyond 2 million km, hide everything except planets
            return {
                color: this.colorTheme.deselected,
                pickable: Pickable.No,
            };
        }
        if (obj.isNotional()) {
            return {
                color: this.colorTheme.deselected,
                pickable: Pickable.No,
            };
        }
        if (obj.isStar()) {
            return this.starColor_(obj);
        }
        // If we are in astronomy mode, hide everything that isn't a star (above)
        if (keepTrackApi.getMainCamera().cameraType === CameraType.ASTRONOMY) {
            return {
                color: this.colorTheme.deselected,
                pickable: Pickable.No,
            };
        }
        const checkFacility = this.checkFacility_(obj);
        if (checkFacility) {
            return checkFacility;
        }
        if (obj.isMarker()) {
            return this.getMarkerColor_();
        }
        if (obj.isSensor() && (this.objectTypeFlags.sensor === false || keepTrackApi.getMainCamera().cameraType === CameraType.PLANETARIUM)) {
            return {
                color: this.colorTheme.deselected,
                pickable: Pickable.No,
            };
        }
        if (obj.isSensor()) {
            return {
                color: this.colorTheme.sensor,
                pickable: Pickable.Yes,
            };
        }
        if (obj.isMissile()) {
            return this.missileColor_(obj);
        }
        if (obj.type === SpaceObjectType.PAYLOAD) {
            if (!settingsManager.isShowPayloads) {
                return {
                    color: this.colorTheme.deselected,
                    pickable: Pickable.No,
                };
            }
        }
        else if (obj.type === SpaceObjectType.ROCKET_BODY) {
            if (!settingsManager.isShowRocketBodies) {
                return {
                    color: this.colorTheme.deselected,
                    pickable: Pickable.No,
                };
            }
        }
        else if (obj.type === SpaceObjectType.DEBRIS) {
            if (!settingsManager.isShowDebris) {
                return {
                    color: this.colorTheme.deselected,
                    pickable: Pickable.No,
                };
            }
        }
        const catalogManagerInstance = keepTrackApi.getCatalogManager();
        const sensorManagerInstance = keepTrackApi.getSensorManager();
        const dotsManagerInstance = keepTrackApi.getDotsManager();
        const sat = obj;
        if (((!dotsManagerInstance.inViewData || (dotsManagerInstance.inViewData && dotsManagerInstance.inViewData?.[sat.id] === 0)) &&
            sat.type === SpaceObjectType.PAYLOAD &&
            this.objectTypeFlags.payload === false) ||
            (keepTrackApi.getMainCamera().cameraType === CameraType.PLANETARIUM && sat.type === SpaceObjectType.PAYLOAD && this.objectTypeFlags.payload === false) ||
            (catalogManagerInstance.isSensorManagerLoaded &&
                sensorManagerInstance.currentSensors[0].type === SpaceObjectType.OBSERVER &&
                typeof sat.vmag === 'undefined' &&
                sat.type === SpaceObjectType.PAYLOAD &&
                this.objectTypeFlags.payload === false)) {
            return {
                color: this.colorTheme.deselected,
                pickable: Pickable.No,
            };
        }
        if (((!dotsManagerInstance.inViewData || (dotsManagerInstance.inViewData && dotsManagerInstance.inViewData?.[sat.id] === 0)) &&
            sat.type === SpaceObjectType.ROCKET_BODY &&
            this.objectTypeFlags.rocketBody === false) ||
            (keepTrackApi.getMainCamera().cameraType === CameraType.PLANETARIUM && sat.type === SpaceObjectType.ROCKET_BODY && this.objectTypeFlags.rocketBody === false) ||
            (catalogManagerInstance.isSensorManagerLoaded &&
                sensorManagerInstance.currentSensors[0].type === SpaceObjectType.OBSERVER &&
                typeof sat.vmag === 'undefined' &&
                sat.type === SpaceObjectType.ROCKET_BODY &&
                this.objectTypeFlags.rocketBody === false)) {
            return {
                color: this.colorTheme.deselected,
                pickable: Pickable.No,
            };
        }
        if (((!dotsManagerInstance.inViewData || (dotsManagerInstance.inViewData && dotsManagerInstance.inViewData?.[sat.id] === 0)) &&
            sat.type === SpaceObjectType.DEBRIS &&
            this.objectTypeFlags.debris === false) ||
            (keepTrackApi.getMainCamera().cameraType === CameraType.PLANETARIUM && sat.type === SpaceObjectType.DEBRIS && this.objectTypeFlags.debris === false) ||
            (catalogManagerInstance.isSensorManagerLoaded &&
                sensorManagerInstance.currentSensors[0].type === SpaceObjectType.OBSERVER &&
                typeof sat.vmag === 'undefined' &&
                sat.type === SpaceObjectType.DEBRIS &&
                this.objectTypeFlags.debris === false)) {
            return {
                color: this.colorTheme.deselected,
                pickable: Pickable.No,
            };
        }
        // NOTE: Treat TBA Satellites as SPECIAL if SCC NUM is less than 70000 (ie a real satellite)
        if (((!dotsManagerInstance.inViewData || (dotsManagerInstance.inViewData && dotsManagerInstance.inViewData?.[sat.id] === 0)) &&
            (sat.type === SpaceObjectType.SPECIAL || sat.type === SpaceObjectType.UNKNOWN || sat.type === SpaceObjectType.NOTIONAL) &&
            this.objectTypeFlags.pink === false) ||
            (keepTrackApi.getMainCamera().cameraType === CameraType.PLANETARIUM &&
                (sat.type === SpaceObjectType.SPECIAL || sat.type === SpaceObjectType.UNKNOWN || sat.type === SpaceObjectType.NOTIONAL) &&
                this.objectTypeFlags.pink === false) ||
            (catalogManagerInstance.isSensorManagerLoaded &&
                sensorManagerInstance.currentSensors[0].type === SpaceObjectType.OBSERVER &&
                typeof sat.vmag === 'undefined' &&
                (sat.type === SpaceObjectType.SPECIAL || sat.type === SpaceObjectType.UNKNOWN || sat.type === SpaceObjectType.NOTIONAL) &&
                this.objectTypeFlags.pink === false)) {
            return {
                color: this.colorTheme.deselected,
                pickable: Pickable.No,
            };
        }
        if (dotsManagerInstance.inViewData?.[sat.id] === 1 && this.objectTypeFlags.inFOV === false && keepTrackApi.getMainCamera().cameraType !== CameraType.PLANETARIUM) {
            return {
                color: this.colorTheme.deselected,
                pickable: Pickable.No,
            };
        }
        if (dotsManagerInstance.inViewData?.[sat.id] === 1 && keepTrackApi.getMainCamera().cameraType !== CameraType.PLANETARIUM) {
            if (catalogManagerInstance.isSensorManagerLoaded && sensorManagerInstance.currentSensors[0].type === SpaceObjectType.OBSERVER && typeof sat.vmag === 'undefined') {
                // Intentional
            }
            else {
                return {
                    color: this.colorTheme.inFOV,
                    pickable: Pickable.Yes,
                };
            }
        }
        let color;
        if (sat.country === 'ANALSAT') {
            color = this.colorTheme.analyst;
        }
        else if (sat.type === SpaceObjectType.PAYLOAD) {
            // Payload
            color = this.colorTheme.payload;
        }
        else if (sat.type === SpaceObjectType.ROCKET_BODY) {
            // Rocket Body
            color = this.colorTheme.rocketBody;
        }
        else if (sat.type === SpaceObjectType.DEBRIS) {
            // Debris
            color = this.colorTheme.debris;
        }
        else if (sat.type === SpaceObjectType.SPECIAL || sat.type === SpaceObjectType.UNKNOWN) {
            // Special Object
            color = this.colorTheme.pink;
        }
        else if (sat.type === SpaceObjectType.NOTIONAL) {
            color = this.colorTheme.notional;
        }
        else {
            color = this.colorTheme.unknown;
        }
        if (typeof color === 'undefined') {
            errorManagerInstance.info(`${sat.id.toString()} has no color!`);
            return {
                color: settingsManager.colors.transparent ?? this.colorTheme.transparent,
                pickable: Pickable.No,
            };
        }
        return {
            color,
            pickable: Pickable.Yes,
        };
    }
    updateGroup(obj) {
        // Show Things in the Group
        if (keepTrackApi.getGroupsManager().selectedGroup?.hasObject(obj.id)) {
            if (obj.isMissile()) {
                return this.missileColor_(obj);
            }
            let color;
            switch (obj.type) {
                case SpaceObjectType.PAYLOAD:
                    color = this.colorTheme.payload;
                    break;
                case SpaceObjectType.ROCKET_BODY:
                    color = this.colorTheme.rocketBody;
                    break;
                case SpaceObjectType.DEBRIS:
                    color = this.colorTheme.debris;
                    break;
                case SpaceObjectType.SPECIAL:
                    color = this.colorTheme.payload; // Assume Payload
                    break;
                case SpaceObjectType.UNKNOWN:
                    color = this.colorTheme.debris; // Assume Debris
                    break;
                default: // Assume Payload
                    color = this.colorTheme.payload;
                    break;
            }
            if (keepTrackApi.getDotsManager().inViewData?.[obj.id] === 1) {
                color = this.colorTheme.inFOV;
            }
            return {
                color,
                pickable: Pickable.Yes,
            };
        }
        if (obj.isMarker()) {
            return this.getMarkerColor_();
        }
        if (obj.isStar()) {
            return this.starColor_(obj);
        }
        // Hide Everything Else
        return {
            color: settingsManager.colors.transparent ?? this.colorTheme.transparent,
            pickable: Pickable.No,
        };
    }
    static layersHtml = html `
  <ul id="layers-list-default-sensor">
    <li>
      <div class="Square-Box layers-payload-box"></div>
      Payload
    </li>
    <li>
      <div class="Square-Box layers-rocketBody-box"></div>
      Rocket Body
    </li>
    <li>
      <div class="Square-Box layers-debris-box"></div>
      Debris
    </li>
    <li>
      <div class="Square-Box layers-pink-box"></div>
      Special Sats
    </li>
    <li>
      <div class="Square-Box layers-inFOV-box"></div>
      Satellite In View
    </li>
    <li>
      <div class="Square-Box layers-missile-box"></div>
      Missile
    </li>
    <li>
      <div class="Square-Box layers-missileInview-box"></div>
      Missile In View
    </li>
    <li>
      <div class="Square-Box layers-sensor-box"></div>
      Sensor
    </li>
    <li>
      <div class="Square-Box layers-facility-box"></div>
      Launch Site
    </li>
  </ul>
  `.trim();
}
